<template>
  <div id="view">
    <historyTag :historyArr="historyArr"></historyTag>
    <div id="mainBox" class="pageMainBox">
      <div class="sysSettingShell">
        <aside class="sysSettingAside">
          <div class="asideHead">配置分组</div>
          <ul class="groupNav">
            <li
              v-for="g in groupMenuItems"
              :key="g.index"
              :class="{ active: selectedGroupKey === g.index }"
              @click="selectedGroupKey = g.index"
            >
              {{ g.label }}
            </li>
          </ul>
        </aside>

        <section class="sysSettingPane">
          <div v-if="displayRows.length" class="settingFormList">
            <div v-for="row in displayRows" :key="row.id" class="settingBlock">
              <div class="settingHead">
                <div class="settingTitleRow">
                  <span class="settingTitle">{{ displayTitle(row) }}</span>
                </div>
                <code class="settingKeyHint">{{ row.settingKey }}</code>
              </div>

              <div class="settingBody">
                <template v-if="isConsignPromoKey(row.settingKey)">
                  <div class="switchCell">
                    <span class="switchLabel">补贴活动</span>
                    <el-switch
                      :value="row.status === 0"
                      :disabled="!!row._statusSaving"
                      @change="(val) => onConsignPromoStatusToggle(row, val)"
                    />
                    <span class="switchTxt">{{ row.status === 0 ? "已启用（使用 consign_listing_price + 折后 price）" : "已停用（仅以 price 为准）" }}</span>
                  </div>
                  <p class="fieldTip">关闭活动后，新改价/上架不再写入 consign_listing_price；建议同步执行批量恢复 SQL（见迁移脚本说明）。</p>
                  <div class="discountRow">
                    <span class="switchLabel">买家折扣</span>
                    <el-input
                      v-model="row.settingValue"
                      placeholder="0.96"
                      class="discountInput"
                      :disabled="row.status !== 0"
                    />
                    <span class="discountHint">相对货主售卖价，如 0.96 = 96 折</span>
                  </div>
                  <div class="rowActions">
                    <el-button
                      type="primary"
                      size="small"
                      :loading="!!row._saving"
                      :disabled="row.status !== 0"
                      @click="saveRow(row)"
                    >保存折扣</el-button>
                  </div>
                </template>

                <template v-else-if="isBooleanSwitchKey(row.settingKey)">
                  <div class="switchCell">
                    <el-switch
                      :value="toggleValueNormalized(row.settingValue)"
                      active-value="1"
                      inactive-value="0"
                      :disabled="!!row._switchSaving"
                      @change="(val) => onTogglePersist(row, val)"
                    />
                    <span class="switchTxt">{{ toggleValueNormalized(row.settingValue) === "1" ? switchOnText(row.settingKey) : switchOffText(row.settingKey) }}</span>
                  </div>
                  <p class="fieldTip">切换后立即生效并保存。</p>
                </template>

                <template v-else-if="(row.valueType || '').toUpperCase() === 'IMAGE'">
                  <div class="uploadHint">二维码 / 图片（上传后填入链接）</div>
                  <div class="uploadBox">
                    <elUpload @getImg="(url) => onRowImg(row, url)" :image="row.settingValue || ''"></elUpload>
                  </div>
                  <el-input v-model="row.settingValue" type="textarea" :rows="2" placeholder="图片 URL" />
                  <div class="rowActions">
                    <el-button type="primary" size="small" :loading="!!row._saving" @click="saveRow(row)">保存</el-button>
                  </div>
                </template>

                <template v-else>
                  <el-input v-model="row.settingValue" type="textarea" :rows="4" placeholder="配置值" class="valueInput" />
                  <div class="rowActions">
                    <el-button type="primary" size="small" :loading="!!row._saving" @click="saveRow(row)">保存</el-button>
                  </div>
                </template>

              </div>
            </div>
          </div>

          <el-empty v-else description="当前分组暂无配置项" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import elUpload from "@/components/elUpload";

/** 配置键对应布尔开关（存库仍为 1/0 字符串） */
const BOOLEAN_SWITCH_META = {
  "secondhand.content_audit.enabled": {
    on: "已开启审核",
    off: "已关闭审核",
  },
};

const CONSIGN_PROMO_KEY = "consign.promo.buyer_discount_rate";

const MODULE_LABEL = {
  app: "小程序",
  jury: "陪审 / 法官",
  secondhand: "闲置",
  consignment: "官方寄售",
};

const GROUP_UNGROUPED = "__ungrouped__";

export default {
  components: { elUpload },
  data() {
    return {
      historyArr: ["系统管理", "配置管理"],
      allRows: [],
      selectedGroupKey: null,
    };
  },
  computed: {
    /** 左侧分组：启用项 + 官方寄售补贴（停用后仍需可见以便重新开启） */
    groupMenuItems() {
      const keys = new Set();
      this.allRows.forEach((r) => {
        if (r.status === 1 && !this.isConsignPromoKey(r.settingKey)) return;
        const m = (r.module || "").trim();
        keys.add(m ? m : GROUP_UNGROUPED);
      });
      const rest = [...keys].filter((k) => k !== GROUP_UNGROUPED);
      rest.sort((a, b) => {
        const oa = Object.keys(MODULE_LABEL).indexOf(a);
        const ob = Object.keys(MODULE_LABEL).indexOf(b);
        if (oa !== -1 || ob !== -1) {
          if (oa === -1) return 1;
          if (ob === -1) return -1;
          return oa - ob;
        }
        return a.localeCompare(b, "zh");
      });
      const items = [];
      rest.forEach((k) => {
        items.push({ index: k, label: this.groupDisplayLabel(k) });
      });
      if (keys.has(GROUP_UNGROUPED)) {
        items.push({ index: GROUP_UNGROUPED, label: "未分组" });
      }
      return items;
    },
    filteredRows() {
      let rows = this.allRows.slice();
      if (this.selectedGroupKey == null || this.selectedGroupKey === "") {
        return [];
      }
      if (this.selectedGroupKey === GROUP_UNGROUPED) {
        rows = rows.filter((r) => !(r.module && String(r.module).trim()));
      } else {
        rows = rows.filter((r) => (r.module || "").trim() === this.selectedGroupKey);
      }
      rows = rows.filter((r) => r.status !== 1 || this.isConsignPromoKey(r.settingKey));
      return rows;
    },
    displayRows() {
      return this.filteredRows.slice().sort((a, b) => String(a.settingKey || "").localeCompare(String(b.settingKey || "")));
    },
  },
  mounted() {
    this.loadList();
  },
  methods: {
    groupDisplayLabel(moduleKey) {
      if (moduleKey === GROUP_UNGROUPED) return "未分组";
      return MODULE_LABEL[moduleKey] ? `${MODULE_LABEL[moduleKey]}（${moduleKey}）` : moduleKey;
    },
    displayTitle(row) {
      const rmk = (row.remark || "").trim();
      if (rmk) return rmk;
      return row.settingKey || "（无键名）";
    },
    isBooleanSwitchKey(key) {
      return key != null && Object.prototype.hasOwnProperty.call(BOOLEAN_SWITCH_META, String(key).trim());
    },
    isConsignPromoKey(key) {
      return String(key || "").trim() === CONSIGN_PROMO_KEY;
    },
    toggleValueNormalized(v) {
      if (v === null || v === undefined || String(v).trim() === "") return "1";
      const s = String(v).trim().toLowerCase();
      if (["0", "false", "off", "no", "disabled", "关", "关闭", "否"].indexOf(s) !== -1) return "0";
      return "1";
    },
    switchOnText(key) {
      const m = BOOLEAN_SWITCH_META[String(key || "").trim()];
      return (m && m.on) || "开";
    },
    switchOffText(key) {
      const m = BOOLEAN_SWITCH_META[String(key || "").trim()];
      return (m && m.off) || "关";
    },
    onRowImg(row, url) {
      this.$set(row, "settingValue", url);
    },
    onConsignPromoStatusToggle(row, enabled) {
      const nextStatus = enabled ? 0 : 1;
      const prevStatus = row.status;
      row.status = nextStatus;
      this.$set(row, "_statusSaving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: row.settingValue,
        valueType: row.valueType || "STRING",
        module: row.module || "consignment",
        remark: row.remark || "",
        status: nextStatus,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success(enabled ? "补贴活动已启用" : "补贴活动已停用");
          } else {
            row.status = prevStatus;
            this.$message.error(res.data.msg || "更新失败");
          }
        })
        .catch(() => {
          row.status = prevStatus;
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_statusSaving", false);
        });
    },
    onTogglePersist(row, val) {
      const next = val === "0" || val === 0 ? "0" : "1";
      const prev = row.settingValue;
      row.settingValue = next;
      this.$set(row, "_switchSaving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: next,
        valueType: row.valueType || "STRING",
        module: row.module || "",
        remark: row.remark || "",
        status: 0,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success("已更新");
          } else {
            row.settingValue = prev;
            this.$message.error(res.data.msg || "更新失败");
          }
        })
        .catch(() => {
          row.settingValue = prev;
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_switchSaving", false);
        });
    },
    saveRow(row) {
      this.$set(row, "_saving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: row.settingValue,
        valueType: row.valueType || "STRING",
        module: row.module || "",
        remark: row.remark || "",
        status: row.status != null ? row.status : 0,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success("保存成功");
          } else {
            this.$message.error(res.data.msg || "保存失败");
          }
        })
        .catch(() => {
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_saving", false);
        });
    },
    ensureSelectedGroup() {
      const items = this.groupMenuItems;
      if (!items.length) {
        this.selectedGroupKey = null;
        return;
      }
      const hit = items.some((x) => x.index === this.selectedGroupKey);
      if (!hit) {
        this.selectedGroupKey = items[0].index;
      }
    },
    loadList() {
      const obj = { page: 1, size: 500 };
      this.$https(this.$api.api.selSysSetting, obj, "POST").then((res) => {
        if (res.data.code === 200) {
          this.allRows = res.data.data || [];
          this.$nextTick(() => {
            this.ensureSelectedGroup();
          });
        } else {
          this.$message.error(res.data.msg || "加载失败");
        }
      });
    },
  },
};
</script>

<style scoped>
.pageMainBox {
  background: transparent;
}

.sysSettingShell {
  display: flex;
  align-items: stretch;
  min-height: 520px;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.sysSettingAside {
  width: 216px;
  flex-shrink: 0;
  border-right: 1px solid #ebeef5;
  background: #fafbfc;
}

.asideHead {
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
}

.groupNav {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.groupNav li {
  padding: 11px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  border-left: 3px solid transparent;
  transition: background 0.15s ease, color 0.15s ease;
}

.groupNav li:hover {
  background: #ecf5ff;
  color: #409eff;
}

.groupNav li.active {
  background: #ecf5ff;
  color: #409eff;
  font-weight: 500;
  border-left-color: #409eff;
}

.sysSettingPane {
  flex: 1;
  min-width: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.settingFormList {
  max-width: 720px;
}

.settingBlock {
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;
}

.settingBlock:last-child {
  border-bottom: none;
}

.settingHead {
  margin-bottom: 10px;
}

.settingTitleRow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.settingTitle {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.settingKeyHint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
}

.settingBody {
  padding-left: 2px;
}

.valueInput {
  max-width: 640px;
}

.rowActions {
  margin-top: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.fieldTip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.uploadHint {
  font-size: 12px;
  color: #606266;
  margin-bottom: 6px;
}

.uploadBox {
  width: 200px;
  height: 120px;
  margin-bottom: 10px;
}

.switchCell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.switchTxt {
  font-size: 13px;
  color: #606266;
}

.switchLabel {
  font-size: 13px;
  color: #303133;
  min-width: 72px;
}

.discountRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.discountInput {
  max-width: 120px;
}

.discountHint {
  font-size: 12px;
  color: #909399;
}
</style>



// WEBPACK FOOTER //
// src/pages/sysSetting/index.vue